import { useState, useEffect } from 'react';

const MOBILE_BREAKPOINT = 768;

const getMediaQueryList = (query: string): MediaQueryList | null => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }

  try {
    return window.matchMedia(query) ?? null;
  } catch {
    return null;
  }
};

export function useMobile() {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    const mql = getMediaQueryList(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    if (mql) return mql.matches;
    return typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false;
  });

  useEffect(() => {
    const mql = getMediaQueryList(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    if (!mql) return;

    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    // Modern browsers
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
    } else if (typeof mql.addListener === 'function') {
      // Legacy browsers
      mql.addListener(onChange);
    }

    // Set initial value
    setIsMobile(mql.matches);

    return () => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', onChange);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(onChange);
      }
    };
  }, []);

  return isMobile;
}

export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState<boolean>(() => {
    const mql = getMediaQueryList(query);
    return mql ? mql.matches : false;
  });

  useEffect(() => {
    const mql = getMediaQueryList(query);
    if (!mql) return;

    const onChange = (e: MediaQueryListEvent) => {
      setMatches(e.matches);
    };

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
    }

    setMatches(mql.matches);

    return () => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', onChange);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(onChange);
      }
    };
  }, [query]);

  return matches;
}

// Alias for backward compatibility
export const useIsMobile = useMobile;
